import OperatorSection from "./DocumentationOperatorSection";

export default function Boolean() {
  return (
    <div className="space-y-5 pb-4 text-neutral-950">
      <h1 className="text-lg leading-6 font-bold -tracking-wide">
        Boolean Operations Guide
      </h1>
      <div className="space-y-1">
        <h1
          id="overview"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Overview
        </h1>
        <p className="leading-[140% text-sm font-medium tracking-normal">
          Boolean operations form the foundation of logical processing in
          programming and database queries. Understanding AND, OR, and NOT
          operators is essential for effective data filtering and conditional
          logic implementation.
        </p>
      </div>
      <OperatorSection
        id="and-operator"
        title="AND Operator"
        description={
          <>
            The AND operator returns TRUE only when{" "}
            <span className="font-bold">all</span> conditions are true.
          </>
        }
        sampleQueries={[
          'Find words "cat" AND "dog" in the text',
          'Find words "pizza" AND "delivery" in the review',
          'Find words "meeting" AND "Monday" in the email',
        ]}
        correctExamples={[
          {
            text: '"I love my cat and my dog plays in the yard"',
            results: ['Contains "cat" AND "dog" = TRUE'],
          },
          {
            text: '"Best pizza place with fast delivery service"',
            results: ['Contains "pizza" AND "delivery" = TRUE'],
          },
          {
            text: '"Don\'t forget our meeting on Monday morning"',
            results: ['Contains "meeting" AND "Monday" = TRUE'],
          },
        ]}
        incorrectExamples={[
          {
            text: '"I love my cat but birds are noisy"',
            results: ['Contains "cat" AND "dog" = FALSE', "“dog” is missing"],
          },
          {
            text: '"Great burger joint with quick delivery"',
            results: [
              'Contains "pizza" AND "delivery" = FALSE',
              "“pizza” is missing",
            ],
          },
          {
            text: '"The meeting was rescheduled to Tuesday"',
            results: [
              'Contains "meeting" AND "Monday" = FALSE',
              "“Monday” is missing",
            ],
          },
        ]}
      />
      <OperatorSection
        id="or-operator"
        title="OR Operator"
        description={
          <>The OR operator returns TRUE when at least one condition is true.</>
        }
        sampleQueries={[
          'Find word "coffee" OR "tea" in the menu',
          'Find word "urgent" OR "important" in the message',
          'Find word "sale" OR "discount" in the advertisement',
        ]}
        correctExamples={[
          {
            text: '"We serve fresh coffee every morning"',
            results: ['Contains "coffee" OR "tea" = TRUE', 'Found "coffee"'],
          },
          {
            text: '"This is an urgent matter requiring attention"',
            results: [
              'Contains "urgent" OR "important" = TRUE',
              'Found "urgent"',
            ],
          },
          {
            text: '"Big sale happening this weekend only"',
            results: ['Contains "sale" OR "discount" = TRUE', ' Found "sale"'],
          },
        ]}
        incorrectExamples={[
          {
            text: '"We serve fresh juice and smoothies"',
            results: [
              'Contains "coffee" OR "tea" = FALSE',
              "Neither word found",
            ],
          },
          {
            text: '"Please review this document when possible"',
            results: [
              'Contains "urgent" OR "important" = FALSE',
              "Neither word found",
            ],
          },
          {
            text: '"New products available in our store"',
            results: [
              'Contains "sale" OR "discount" = FALSE ',
              "Neither word found",
            ],
          },
        ]}
      />
      <OperatorSection
        id="not-operator"
        title="NOT Operator"
        description={
          <>
            The NOT operator flips the result (TRUE becomes FALSE, FALSE becomes
            TRUE).
          </>
        }
        sampleQueries={[
          'Text does NOT contain "spam"',
          'Article does NOT contain "error"',
          'Review does NOT contain "bad"',
        ]}
        correctExamples={[
          {
            text: '"Welcome to our newsletter with great deals"',
            results: [
              'NOT contains "spam" = TRUE ',
              'No "spam" found - legitimate email',
            ],
          },
          {
            text: '"This is an urgent matter requiring attention"',
            results: [
              'NOT contains "error" = TRUE ',
              'No "error" found - system working',
            ],
          },
          {
            text: '"Big sale happening this weekend only"',
            results: [
              'NOT contains "bad" = TRUE ',
              'No "bad" found - positive review',
            ],
          },
        ]}
        incorrectExamples={[
          {
            text: '"This spam email is trying to trick you"',
            results: [
              'NOT contains "spam" = FALSE ',
              '"spam" found - suspicious email',
            ],
          },
          {
            text: '"System error occurred during processing"',
            results: [
              'NOT contains "error" = FALSE ',
              '"error" found - system problem',
            ],
          },
          {
            text: '"New products available in our store"',
            results: [
              'NOT contains "bad" = FALSE ',
              '"bad" found - negative review',
            ],
          },
        ]}
      />
      <OperatorSection
        id="combined-ops"
        title="Combined Boolean Operations (AND, OR, NOT)"
        description={
          <>Using multiple operators together for complex search conditions.</>
        }
        sampleQueries={[
          'Find "phone" AND "repair" AND NOT "expensive"',
          'Find ("manager" OR "supervisor") AND NOT "unavailable"',
          'Find "restaurant" AND ("pizza" OR "pasta") AND NOT "closed"',
        ]}
        correctExamples={[
          {
            text: '"Phone repair service available at affordable prices"',
            results: [
              '"phone" AND "repair" AND NOT "expensive" = TRUE',
              'Has "phone" and "repair", doesn\'t have "expensive"',
            ],
          },
          {
            text: '"The manager is ready to help with your concerns"',
            results: [
              '("manager" OR "supervisor") AND NOT "unavailable" = TRUE',
              'Has "manager", doesn\'t have "unavailable"',
            ],
          },
          {
            text: '"Italian restaurant serving fresh pizza and salads"',
            results: [
              '"restaurant" AND ("pizza" OR "pasta") AND NOT "closed" = TRUE',
              'Has "restaurant" and "pizza", doesn\'t have "closed"',
            ],
          },
        ]}
        incorrectExamples={[
          {
            text: '"Phone repair service but it\'s quite expensive"',
            results: [
              '"phone" AND "repair" AND NOT "expensive" = FALSE',
              'Has all required words but also has "expensive"',
            ],
          },
          {
            text: '"The supervisor is currently unavailable for meetings"',
            results: [
              '("manager" OR "supervisor") AND NOT "unavailable" = FALSE ',
              'Has "supervisor" but also has "unavailable"',
            ],
          },
          {
            text: '"The restaurant is temporarily closed for renovation"',
            results: [
              '"restaurant" AND ("pizza" OR "pasta") AND NOT "closed" = FALSE',
              '(Has "restaurant" but missing "pizza"/"pasta" and has "closed")',
            ],
          },
        ]}
      />
    </div>
  );
}
